import { versionBox, createLayoutBox } from '../../../utils/privateLayouts';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  savePageTextEntries as savePageTextEntriesRequest,
  savePageTextItem,
  savePageTextItems,
} from '../api/pageApi';
import { LANGUAGE_LABELS } from '../model/constants';

const ALL_LANGUAGES = Object.keys(LANGUAGE_LABELS);
const MAIN_TEXT_ROLE = 'main';
const ADDITIONAL_TEXT_ROLE = 'additional';
const PAGE_MAIN_SOURCE_ID = 'page-main';
const PAGE_MAIN_SOURCE_TYPE = 'page_main';
const PAGE_TEXT_ENTRY_SOURCE_TYPE = 'page_text_entry';
const SHARED_PAGE_LANGUAGE = '__page_shared__';

const createPageTextEntryId = () =>
  `page-text-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeSavedTextItems = (items = []) => items.map((item) => {
  const normalizedVisibility = { ...item, visibleOnCanvas: item.visibleOnCanvas !== false };
  const isMain = item.role === MAIN_TEXT_ROLE ||
    item.sourceType === PAGE_MAIN_SOURCE_TYPE ||
    item.sourceTextEntryId === PAGE_MAIN_SOURCE_ID ||
    (!item.role && (item.style?.presetFlow || item.style?.linkedToPreset) &&
      (!item.sourceTextEntryId || item.sourceTextEntryId === PAGE_MAIN_SOURCE_ID));
  if (isMain) {
    return {
      ...normalizedVisibility,
      role: MAIN_TEXT_ROLE,
      sourceType: PAGE_MAIN_SOURCE_TYPE,
      sourceTextEntryId: PAGE_MAIN_SOURCE_ID,
    };
  }
  if (item.role === ADDITIONAL_TEXT_ROLE || item.sourceType === PAGE_TEXT_ENTRY_SOURCE_TYPE) {
    return { ...normalizedVisibility, role: ADDITIONAL_TEXT_ROLE, sourceType: PAGE_TEXT_ENTRY_SOURCE_TYPE };
  }
  return { ...normalizedVisibility, role: ADDITIONAL_TEXT_ROLE, sourceType: item.sourceType || 'manual' };
});

export default function usePageTextItems({
  canvasSize,
  bookDefaultPresetId,
  currentLanguages,
  designPresets = [],
  getCurrentTextValues,
  mainTextDisplayMode,
  notify,
  pageId,
  setPageData,
  setSelectedArtworkId,
  textItemsRef: providedTextItemsRef,
}) {
  const [textItems, setTextItems] = useState([]);
  const localTextItemsRef = useRef([]);
  const textItemsRef = providedTextItemsRef || localTextItemsRef;
  const [pageTextEntries, setPageTextEntries] = useState([]);
  const [selectedTextItemId, setSelectedTextItemId] = useState(null);
  const [selectedAdditionalEntryId, setSelectedAdditionalEntryId] = useState('');
  const [savingTextItems, setSavingTextItems] = useState(false);
  const [savingTextEntries, setSavingTextEntries] = useState(false);
  const [showAdditionalContentModal, setShowAdditionalContentModal] = useState(false);
  const [additionalContentDraft, setAdditionalContentDraft] = useState({ label: '', texts: {} });
  const additionalContentDisplayMode = 'both';

  const isMainItem = useCallback(
    (item) => item.role === MAIN_TEXT_ROLE,
    [],
  );

  const normalizePageTextEntries = useCallback((entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return [];
    return entries.map((entry, index) => ({
      id: entry.id || createPageTextEntryId(),
      label: entry.label || `Item ${index + 1}`,
      sourceType: PAGE_TEXT_ENTRY_SOURCE_TYPE,
      texts: { ...(entry.texts || {}) },
    }));
  }, []);

  useEffect(() => {
    textItemsRef.current = textItems;
  }, [textItems, textItemsRef]);

  useEffect(() => {
    if (pageTextEntries.length > 0 && !pageTextEntries.some((entry) => entry.id === selectedAdditionalEntryId)) {
      setSelectedAdditionalEntryId(pageTextEntries[0].id);
    }
    if (pageTextEntries.length === 0 && selectedAdditionalEntryId) setSelectedAdditionalEntryId('');
  }, [pageTextEntries, selectedAdditionalEntryId, textItemsRef]);

  useEffect(() => {
    const entryById = new Map(pageTextEntries.map((entry) => [entry.id, entry.texts || {}]));
    const mainTexts = getCurrentTextValues();
    setTextItems((current) => current.map((item) => {
      const texts = item.role === MAIN_TEXT_ROLE ? mainTexts : entryById.get(item.sourceTextEntryId);
      return texts ? { ...item, texts } : item;
    }));
  }, [getCurrentTextValues, pageTextEntries, textItemsRef]);

  const saveTextItems = useCallback(async (itemsToSave = textItems, {
    notifyMessage = 'Format saved.',
    preserveLocalItems = false,
  } = {}) => {
    if (!pageId) return null;
    setSavingTextItems(true);
    try {
      // The page owns every positioned box, including Main text placement.
      // Wording remains in page fields and page_text_entries.
      const itemsToPersist = itemsToSave.map((item) => ({ ...versionBox(item), pageLanguage: SHARED_PAGE_LANGUAGE }));
      const response = await savePageTextItems(pageId, itemsToPersist);
      const draftWording = Object.fromEntries(Object.entries(getCurrentTextValues()).map(([language, text]) => [`text_${language}`, text]));
      setPageData((current) => current?._id === pageId
        ? { ...current, ...draftWording, text_items: response.data.text_items || [], layout_version: 1 }
        : current);
      const submittedTexts = new Map(itemsToPersist.map((item) => [item.id, item.texts]));
      const savedItems = normalizeSavedTextItems(response.data.text_items || []).map((item) => ({
        ...item,
        texts: textItemsRef.current.find((current) => current.id === item.id)?.texts || submittedTexts.get(item.id) || item.texts || {},
      }));
      const nextLocalItems = savedItems;
      if (!preserveLocalItems) { textItemsRef.current = nextLocalItems; setTextItems(nextLocalItems); }
      if (notifyMessage) notify(notifyMessage);
      return nextLocalItems;
    } catch (error) {
      console.error('Failed to save text items', error);
      notify('Failed to save text items', 'error');
      return null;
    } finally {
      setSavingTextItems(false);
    }
  }, [getCurrentTextValues, notify, pageId, setPageData, textItems, textItemsRef]);

  const saveTextItem = useCallback(async (itemId) => {
    const item = textItemsRef.current.find((candidate) => candidate.id === itemId);
    if (!item || !pageId) return null;
    setSavingTextItems(true);
    try {
      const response = await savePageTextItem(pageId, itemId, versionBox(item));
      setPageData((current) => current?._id === pageId
        ? {
            ...current,
            layout_version: 1,
            text_items: [
              ...(current.text_items || []).filter((savedItem) => savedItem.id !== itemId),
              response.data.text_item,
            ],
          }
        : current);
      // Edits made while the request was pending remain unsaved local drafts.
      const keepDraft = (candidate) => candidate.id === itemId ? versionBox(candidate) : candidate;
      setTextItems((current) => current.map(keepDraft));
      textItemsRef.current = textItemsRef.current.map(keepDraft);
      notify('Text box saved in this page’s private layout.');
      return item;
    } catch (error) {
      console.error('Failed to save text item', error);
      notify('Failed to save text box', 'error');
      return null;
    } finally {
      setSavingTextItems(false);
    }
  }, [notify, pageId, setPageData, textItemsRef]);

  const buildMainCanvasItem = useCallback((displayMode = mainTextDisplayMode) => createLayoutBox({
    sourceId: PAGE_MAIN_SOURCE_ID, texts: getCurrentTextValues(), languages: currentLanguages,
    canvasSize, textVisibility: displayMode, presets: designPresets, bookDefaultId: bookDefaultPresetId,
  }), [bookDefaultPresetId, canvasSize, currentLanguages, designPresets, getCurrentTextValues, mainTextDisplayMode]);

  const insertMainTextBlock = useCallback(async (displayMode = mainTextDisplayMode) => {
    const existingMainItem = textItemsRef.current.find(isMainItem);
    if (existingMainItem) {
      const restored = { ...existingMainItem, visibleOnCanvas: true };
      const next = textItemsRef.current.map((item) => item.id === restored.id ? restored : item);
      textItemsRef.current = next;
      setTextItems(next);
      setSelectedArtworkId(null);
      setSelectedTextItemId(restored.id);
      return restored;
    }
    const createdItem = await buildMainCanvasItem(displayMode);
    if (!createdItem) {
      notify('Cannot insert empty text.', 'error');
      return null;
    }
    const nextItems = [...textItemsRef.current, createdItem];
    textItemsRef.current = nextItems;
    setTextItems(nextItems);
    setSelectedArtworkId(null);
    setSelectedTextItemId(createdItem.id);
    return createdItem;
  }, [buildMainCanvasItem, isMainItem, mainTextDisplayMode, notify, setSelectedArtworkId, textItemsRef]);

  const addMainTextToPage = useCallback(async (displayMode = mainTextDisplayMode) => {
    return insertMainTextBlock(displayMode);
  }, [insertMainTextBlock, mainTextDisplayMode]);

  const getSelectedAdditionalEntry = useCallback((entryId = selectedAdditionalEntryId) =>
    entryId ? pageTextEntries.find((entry) => entry.id === entryId) || null : pageTextEntries[0] || null,
  [pageTextEntries, selectedAdditionalEntryId]);

  const insertAdditionalTextBlock = useCallback(async (entryId = selectedAdditionalEntryId, displayMode = additionalContentDisplayMode, entryOverride = null) => {
    const sourceEntry = entryOverride || getSelectedAdditionalEntry(entryId);
    if (!sourceEntry) {
      notify('Add additional text first.', 'error');
      return null;
    }
    const matchingItems = textItemsRef.current.filter((item) => item.role === ADDITIONAL_TEXT_ROLE && item.sourceTextEntryId === sourceEntry.id);
    const existingItem = matchingItems[0];
    if (existingItem) {
      const restored = { ...existingItem, visibleOnCanvas: true };
      const next = textItemsRef.current.map((item) => item.id === restored.id ? restored : item);
      textItemsRef.current = next;
      setTextItems(next);
      setSelectedAdditionalEntryId(sourceEntry.id);
      setSelectedArtworkId(null);
      setSelectedTextItemId(restored.id);
      return restored;
    }
    const createdItem = createLayoutBox({ sourceId: sourceEntry.id, name: sourceEntry.label,
      texts: sourceEntry.texts || {}, languages: currentLanguages, canvasSize,
      textVisibility: displayMode, presets: designPresets, bookDefaultId: bookDefaultPresetId });
    if (!createdItem) {
      notify('Cannot insert empty additional text.', 'error');
      return null;
    }
    const nextItems = textItemsRef.current
      .filter((item) => !(item.role === ADDITIONAL_TEXT_ROLE && item.sourceTextEntryId === sourceEntry.id))
      .concat({ ...createdItem, pageLanguage: SHARED_PAGE_LANGUAGE, visibleOnCanvas: true });
    textItemsRef.current = nextItems;
    setTextItems(nextItems);
    setSelectedAdditionalEntryId(sourceEntry.id);
    setSelectedArtworkId(null);
    setSelectedTextItemId(createdItem.id);
    return createdItem;
  }, [bookDefaultPresetId, canvasSize, currentLanguages, designPresets, getSelectedAdditionalEntry, notify, selectedAdditionalEntryId, setSelectedArtworkId, textItemsRef]);

  const updatePageTextItemPosition = useCallback((itemId, positionUpdates) => {
    const nextItems = textItemsRef.current.map((item) => item.id === itemId ? {
      ...item,
      position: { ...(item.position || {}), ...positionUpdates },
      style: { ...(item.style || {}), positionMode: 'custom' },
      layoutConfig: { ...item.layoutConfig, positionMode: 'custom' },
    } : item);
    textItemsRef.current = nextItems;
    setTextItems(nextItems);
  }, [textItemsRef]);

  const createEmptyAdditionalContent = useCallback(() => ({
    label: `Text box ${pageTextEntries.length + 1}`,
    texts: ALL_LANGUAGES.reduce((texts, language) => ({ ...texts, [language]: '' }), {}),
  }), [pageTextEntries.length]);

  const openAdditionalContentModal = useCallback(() => {
    setAdditionalContentDraft(createEmptyAdditionalContent());
    setShowAdditionalContentModal(true);
  }, [createEmptyAdditionalContent]);

  const savePageTextEntries = useCallback(async (entriesToSave = pageTextEntries, { notifyMessage = 'Extra text saved.' } = {}) => {
    if (!pageId) return null;
    setSavingTextEntries(true);
    try {
      const response = await savePageTextEntriesRequest(pageId, entriesToSave);
      const normalizedEntries = normalizePageTextEntries(response.data.page_text_entries);
      setPageTextEntries(normalizedEntries);
      if (notifyMessage) notify(notifyMessage);
      return normalizedEntries;
    } catch (error) {
      console.error('Failed to save page texts', error);
      notify('Failed to save extra text', 'error');
      return null;
    } finally {
      setSavingTextEntries(false);
    }
  }, [normalizePageTextEntries, notify, pageId, pageTextEntries]);

  const addPageTextEntry = useCallback(async () => {
    const nextEntry = {
      id: createPageTextEntryId(),
      label: additionalContentDraft.label?.trim() || `Text box ${pageTextEntries.length + 1}`,
      sourceType: PAGE_TEXT_ENTRY_SOURCE_TYPE,
      texts: ALL_LANGUAGES.reduce((texts, language) => ({
        ...texts,
        [language]: additionalContentDraft.texts?.[language] || '',
      }), {}),
    };
    const nextEntries = [...pageTextEntries, nextEntry];
    const savedEntries = await savePageTextEntries(nextEntries);
    if (!savedEntries) return null;
    setSelectedAdditionalEntryId(nextEntry.id);
    setShowAdditionalContentModal(false);
    setAdditionalContentDraft(createEmptyAdditionalContent());
    return savedEntries.find((entry) => entry.id === nextEntry.id) || nextEntry;
  }, [additionalContentDraft, createEmptyAdditionalContent, pageTextEntries, savePageTextEntries]);

  const renamePageTextEntry = useCallback(async (entryId, label) => {
    const entry = pageTextEntries.find((item) => item.id === entryId);
    if (!entry) return false;
    const trimmedLabel = label?.trim();
    if (!trimmedLabel) return false;
    if (trimmedLabel === entry.label) return true;
    const nextEntries = pageTextEntries.map((item) => item.id === entryId ? { ...item, label: trimmedLabel } : item);
    return Boolean(await savePageTextEntries(nextEntries, { notifyMessage: 'Text box renamed.' }));
  }, [pageTextEntries, savePageTextEntries]);

  const updateAdditionalEntryText = useCallback((entryId, language, value) => {
    setPageTextEntries((entries) => entries.map((entry) => entry.id === entryId ? {
      ...entry,
      texts: { ...(entry.texts || {}), [language]: value },
    } : entry));
    setTextItems((items) => items.map((item) => item.sourceTextEntryId === entryId ? {
      ...item,
      texts: { ...(item.texts || {}), [language]: value },
    } : item));
  }, []);

  const deletePageTextEntry = useCallback(async (entryId) => {
    const nextEntries = pageTextEntries.filter((entry) => entry.id !== entryId);
    const nextItems = textItemsRef.current.filter((item) => item.sourceTextEntryId !== entryId);
    // Remove placement first. A failed layout save must never delete wording.
    const saved = await saveTextItems(nextItems, { notifyMessage: null });
    if (!saved || !await savePageTextEntries(nextEntries)) return false;
    textItemsRef.current = saved;
    if (selectedAdditionalEntryId === entryId) setSelectedAdditionalEntryId(nextEntries[0]?.id || '');
    return true;
  }, [pageTextEntries, savePageTextEntries, saveTextItems, selectedAdditionalEntryId, textItemsRef]);


  return {
    ADDITIONAL_TEXT_ROLE,
    MAIN_TEXT_ROLE,
    PAGE_MAIN_SOURCE_ID,
    additionalContentDisplayMode,
    additionalContentDraft,
    addMainTextToPage,
    addPageTextEntry,
    buildMainCanvasItem,
    deletePageTextEntry,
    getSelectedAdditionalEntry,
    insertAdditionalTextBlock,
    insertMainTextBlock,
    isMainItem,
    normalizePageTextEntries,
    normalizeSavedTextItems,
    openAdditionalContentModal,
    pageTextEntries,
    renamePageTextEntry,
    savePageTextEntries,
    saveTextItems,
    saveTextItem,
    savingTextEntries,
    savingTextItems,
    selectedAdditionalEntryId,
    selectedTextItemId,
    setAdditionalContentDraft,
    setShowAdditionalContentModal,
    setPageTextEntries,
    setSelectedAdditionalEntryId,
    setSelectedTextItemId,
    setTextItems,
    showAdditionalContentModal,
    textItems,
    textItemsRef,
    updateAdditionalEntryText,
    updatePageTextItemPosition,
  };
}
